import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { CheckIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core/react/i18n';

type ActionButtonProps = Readonly<{
	isFirstActive: boolean;
	hasCta: boolean;
	isCompleted: boolean;
	primaryActionText: string;
	className?: string;
	dataQaSuffix?: string;
	taskCta?: boolean;
	onClick?: () => void;
}>;

const PRIMARY_BUTTON_CLASSES =
	'text-typography-strong-fixed bg-gradient-to-r from-yellow-400 to-yellow-300 whitespace-nowrap';
const SECONDARY_BUTTON_CLASSES = 'whitespace-nowrap';

/**
 * Renders either a primary action button or a secondary-solid "Done" / follow-up CTA based on task state.
 * Completed tasks always use the Done treatment (checkmark + label), including when the task normally has a CTA,
 * so disabled styling is not fighting the primary gradient.
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
	isFirstActive,
	hasCta,
	isCompleted,
	primaryActionText,
	className = '',
	dataQaSuffix = '',
	taskCta,
	onClick,
}) => {
	const showCta = hasCta && !isCompleted;
	const usePrimaryChrome = isFirstActive && !isCompleted;
	const variant = usePrimaryChrome ? 'primary' : 'secondary-solid';
	const dataQa = showCta ? `dynamic-task-primary-cta${dataQaSuffix}` : `dynamic-task-done-button${dataQaSuffix}`;
	const buttonClasses = usePrimaryChrome ? PRIMARY_BUTTON_CLASSES : SECONDARY_BUTTON_CLASSES;

	return (
		<Button
			variant={variant}
			size="x-small"
			disabled={isCompleted}
			data-qa={dataQa}
			data-cta-button={taskCta}
			className={cn(buttonClasses, className)}
			leadingIcon={showCta ? undefined : <CheckIcon />}
			onClick={onClick}
		>
			{showCta ? (
				primaryActionText
			) : (
				<Translate value="corgi.petition.starter_dashboard_v2.dynamic_tasks.task.done_button" />
			)}
		</Button>
	);
};
